import type { EventName } from "@/api/posthog";
import { RESERVED_PROFILE_SEGMENTS } from "@/domains/freelance/profile/username/rules";

/**
 * I tre eventi della pagina pubblica, emessi per un visitatore **senza
 * sessione** (SPEC public-profile-anonymous, AC16).
 *
 * Un solo punto di verità: la rotta li emette da qui e `before_send`
 * (`src/api/posthog-scrub.ts`) li riconosce da qui. Prima i nomi vivevano in
 * due copie e un rename al call site avrebbe spento lo scrub in silenzio —
 * l'evento sarebbe partito con il referrer intero e nessun test rosso.
 */
export const PUBLIC_PROFILE_EVENT = {
  view: "view_public_profile",
  notFound: "fail_public_profile_not_found",
  load: "fail_public_profile_load",
} as const satisfies Record<string, EventName>;

export type PublicProfileEvent =
  (typeof PUBLIC_PROFILE_EVENT)[keyof typeof PUBLIC_PROFILE_EVENT];

export const PUBLIC_PROFILE_EVENT_NAMES: ReadonlySet<string> = new Set<string>(
  Object.values(PUBLIC_PROFILE_EVENT),
);

/**
 * `/profile/<segmento>` — la pagina pubblica — e nient'altro: non
 * `/profile/me` (il proprietario), non `/profile/company/...`, non
 * `/profile/toolkit` e le altre rotte dell'app, non `/profile` (le tab).
 *
 * Serve allo scrub per riconoscere gli eventi **automatici** di posthog-js
 * (`$pageview`, `$pageleave`, `$autocapture`) emessi sulla stessa pagina: non
 * hanno un nome nostro, ma portano lo stesso referrer intero dei tre eventi
 * dichiarati, e senza questo cancello lo scrub dei tre sarebbe in gran parte
 * illusorio.
 *
 * La lista dei segmenti riservati è la stessa che il backend rifiuta come
 * username: sono le rotte reali dell'app, e prima di questa spec la regex ne
 * escludeva solo `me` — `/profile/toolkit` passava per una pagina pubblica.
 */
const RESERVED: ReadonlySet<string> = new Set(RESERVED_PROFILE_SEGMENTS);

const PUBLIC_PROFILE_PATHNAME = /^\/profile\/([^/]+)\/?$/;

export const isPublicProfilePathname = (pathname: unknown): boolean => {
  if (typeof pathname !== "string") {
    return false;
  }
  const segment = PUBLIC_PROFILE_PATHNAME.exec(pathname)?.[1];
  return segment !== undefined && !RESERVED.has(segment.toLowerCase());
};

/**
 * `/profile/giulia-design` → `/profile/:segment`.
 *
 * Lo username è il nome e cognome di una persona: in `$pathname` e dentro
 * `$current_url` finirebbe in ogni evento automatico, anche quelli emessi da
 * chi guarda il profilo. La pagina resta distinguibile — è ancora la pagina
 * pubblica — senza dire di chi.
 */
export const PUBLIC_PROFILE_PATHNAME_MASK = "/profile/:segment";

export const maskPublicProfilePathname = (pathname: string): string =>
  isPublicProfilePathname(pathname) ? PUBLIC_PROFILE_PATHNAME_MASK : pathname;
